/**
 * Générateur pseudo-aléatoire injecté.
 *
 * `Math.random()` est interdit dans le moteur : l'ordre de révélation doit être
 * reproductible pour une graine donnée, sinon deux joueurs du même défi ne
 * voient pas les mêmes lettres (README §5).
 */
export type Rng = () => number;

/** Hachage 32 bits d'une graine texte (FNV-1a). */
function hashSeed(seed: string): number {
  let hash = 0x811c9dc5;
  for (let index = 0; index < seed.length; index += 1) {
    hash ^= seed.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0;
}

/** Mulberry32 : court, déterministe, suffisant pour un ordre de révélation. */
export function createSeededRng(seed: string | number): Rng {
  let state = (typeof seed === "number" ? seed >>> 0 : hashSeed(seed)) || 1;

  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Mélange de Fisher-Yates, sans mutation de l'entrée. */
export function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const out = [...items];
  for (let index = out.length - 1; index > 0; index -= 1) {
    const swapWith = Math.floor(rng() * (index + 1));
    const a = out[index] as T;
    const b = out[swapWith] as T;
    out[index] = b;
    out[swapWith] = a;
  }
  return out;
}
